import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ApiConsume } from "../_API/ApiConsume";
import { sendAutomationLog } from "../_Utils/automationLog";

const DEFAULT_CACHE_KEY = "WAM_ADDON_STATUS_CACHE";
const DEFAULT_TTL_MS = 5 * 60 * 1000;
const DEFAULT_FOCUS_THROTTLE_MS = 60 * 1000;

const readCache = (key) => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
};

const writeCache = (key, data) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(data));
  } catch {
    // ignore quota errors
  }
};

const clearCache = (key) => {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(key);
  } catch {
    // noop
  }
};

export default function useAddonStatus({
  token,
  isLogged,
  ttlMs = DEFAULT_TTL_MS,
  storageKey = DEFAULT_CACHE_KEY,
  focusThrottleMs = DEFAULT_FOCUS_THROTTLE_MS,
  enableFocusRefresh = true,
} = {}) {
  const [state, setState] = useState(() => ({
    data: {},
    fetchedAt: null,
    isLoading: Boolean(isLogged && token),
    error: null,
  }));

  const stateRef = useRef(state);
  const inflightRef = useRef(null);
  const lastFocusRefreshRef = useRef(0);

  const updateState = useCallback((updater) => {
    setState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : { ...prev, ...updater };
      stateRef.current = next;
      return next;
    });
  }, []);

  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  const performFetch = useCallback(
    async ({ reason = "manual" } = {}) => {
      try {
        const res = await ApiConsume("ADDONS__STATUS", "GET", {}, null, token);
        const payload = res?.data || {};
        const normalized = {
          data: payload,
          fetchedAt: Date.now(),
          isLoading: false,
          error: null,
        };
        updateState(normalized);
        writeCache(storageKey, normalized);
        return payload;
      } catch (error) {
        console.error(`[addons] Error fetching status (${reason}):`, error);
        updateState((prev) => ({
          ...prev,
          isLoading: false,
          error: error?.message || "No se pudo actualizar el estado de los módulos.",
        }));
        return stateRef.current.data;
      }
    },
    [storageKey, token, updateState]
  );

  const refresh = useCallback(
    ({ force = false, reason = "manual" } = {}) => {
      if (!isLogged || !token) {
        updateState({ data: {}, fetchedAt: null, isLoading: false, error: null });
        clearCache(storageKey);
        return Promise.resolve({});
      }
      const now = Date.now();
      const lastFetched = stateRef.current.fetchedAt;
      if (!force && lastFetched && now - lastFetched < ttlMs) {
        return Promise.resolve(stateRef.current.data);
      }
      if (inflightRef.current) {
        return inflightRef.current;
      }
      if (force) {
        sendAutomationLog({
          token,
          module: "addons",
          eventType: "refresh_addons_forced",
          payload: { reason },
        });
      }
      updateState((prev) => ({ ...prev, isLoading: true }));
      const pending = performFetch({ reason }).finally(() => {
        inflightRef.current = null;
      });
      inflightRef.current = pending;
      return pending;
    },
    [isLogged, performFetch, storageKey, ttlMs, token, updateState]
  );

  // Bootstrap (load cache + fetch if stale)
  useEffect(() => {
    if (!isLogged || !token) {
      updateState({ data: {}, fetchedAt: null, isLoading: false, error: null });
      clearCache(storageKey);
      return;
    }

    const cached = readCache(storageKey);
    if (cached && cached.fetchedAt && Date.now() - cached.fetchedAt < ttlMs) {
      updateState({ ...cached, isLoading: false, error: null });
    } else if (cached) {
      updateState({ ...cached, isLoading: true, error: null });
    }
    refresh({ force: !cached, reason: "bootstrap" });
  }, [isLogged, refresh, storageKey, ttlMs, token, updateState]);

  // Focus / visibility refresh (throttled)
  useEffect(() => {
    if (!enableFocusRefresh || !isLogged) return undefined;

    const handler = () => {
      if (typeof document !== "undefined" && document.visibilityState && document.visibilityState !== "visible") {
        return;
      }
      const now = Date.now();
      if (now - lastFocusRefreshRef.current < focusThrottleMs) {
        return;
      }
      lastFocusRefreshRef.current = now;
      refresh({ force: true, reason: "focus" });
    };

    window.addEventListener("focus", handler);
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handler);
    }
    return () => {
      window.removeEventListener("focus", handler);
      if (typeof document !== "undefined") {
        document.removeEventListener("visibilitychange", handler);
      }
    };
  }, [enableFocusRefresh, focusThrottleMs, isLogged, refresh]);

  return useMemo(
    () => ({
      data: state.data,
      fetchedAt: state.fetchedAt,
      isLoading: state.isLoading,
      error: state.error,
      refresh,
    }),
    [refresh, state.data, state.error, state.fetchedAt, state.isLoading]
  );
}
